import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { History, Trash2, Download } from 'lucide-react';
import { format } from 'date-fns';

interface AuditLogEntry {
  action: string;
  patientId: string;
  oldName: string;
  newName: string;
  changedBy: string;
  userRole: string;
  reason: string;
  timestamp: string;
  patientIdFormatted: string;
}

interface NameChangeAuditLogProps {
  isOpen: boolean;
  onClose: () => void;
}

export function NameChangeAuditLog({ isOpen, onClose }: NameChangeAuditLogProps) {
  const [auditLogs, setAuditLogs] = useState<AuditLogEntry[]>([]);

  useEffect(() => {
    if (isOpen) {
      loadAuditLogs();
    }
  }, [isOpen]);

  const loadAuditLogs = () => {
    try {
      const logs = JSON.parse(localStorage.getItem('patient_audit_logs') || '[]');
      setAuditLogs(logs.reverse()); // Show newest first
    } catch (error) {
      console.error('Error loading audit logs:', error);
      setAuditLogs([]);
    }
  };

  const clearAuditLogs = () => {
    if (window.confirm('Are you sure you want to clear all audit logs? This action cannot be undone.')) {
      localStorage.removeItem('patient_audit_logs');
      setAuditLogs([]);
    }
  };

  const exportAuditLogs = () => {
    const csvContent = [
      'Timestamp,Patient ID,Old Name,New Name,Changed By,User Role,Reason',
      ...auditLogs.map(log => 
        `"${log.timestamp}","${log.patientIdFormatted}","${log.oldName}","${log.newName}","${log.changedBy}","${log.userRole}","${log.reason}"`
      ).join('\n')
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `patient-name-changes-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-5xl max-h-[80vh]">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <History className="h-5 w-5" />
              Patient Name Change Audit Log
            </CardTitle>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={exportAuditLogs}
                disabled={auditLogs.length === 0}
              >
                <Download className="h-4 w-4 mr-2" />
                Export CSV
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={clearAuditLogs}
                disabled={auditLogs.length === 0}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Clear Logs
              </Button>
              <Button variant="outline" size="sm" onClick={onClose}>
                Close
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {auditLogs.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <History className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No name changes recorded yet.</p>
            </div>
          ) : (
            <ScrollArea className="h-96">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[100px] text-center">Patient ID</TableHead>
                    <TableHead className="w-[140px] text-center">Timestamp</TableHead>
                    <TableHead className="w-[100px] text-center">Role</TableHead>
                    <TableHead className="w-[350px] text-center">Name Change</TableHead>
                    <TableHead className="text-center">Reason</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {auditLogs.map((log, index) => (
                    <TableRow key={index}>
                      <TableCell className="whitespace-nowrap text-center">
                        <Badge variant="outline" className="text-xs">
                          {log.patientIdFormatted}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground whitespace-nowrap text-center">
                        {format(new Date(log.timestamp), 'MMM dd, HH:mm:ss')}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-center">
                        <Badge variant="secondary" className="text-xs">
                          {log.userRole}
                        </Badge>
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-center">
                        <div className="text-sm">
                          <span className="line-through text-red-600">{log.oldName}</span>
                          <span className="text-muted-foreground mx-1">→</span>
                          <span className="text-green-600 font-medium">{log.newName}</span>
                        </div>
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-center">
                        <span className="bg-gray-50 px-2 py-1 rounded text-xs">
                          {log.reason}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ScrollArea>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
